/**
 * The components a product family can include, each run on an existing IIS server. The one place they are listed: the
 * Add-ons step, the validation, the payload and the Review step all read from here.
 *
 * Traverse (11.0 Standard and 11.2 Global) has four. Process Pro Global has none. Each is a Yes/No question; Yes asks for the
 * existing IIS server it runs on, No clears it. Customer Portal is separate: it is asked of every product line.
 */
import { productFamily, type ProductFamily, type ProductSubject } from '@/data/productLines'

export type TraverseComponentKey = 'wms' | 'travApiPortal' | 'webApiPortal' | 'adminPortal'

export interface ProductComponent {
  key: TraverseComponentKey
  /** The name used in labels, the "To continue:" list and the Review step. */
  name: string
  question: string
}

const component = (key: TraverseComponentKey, name: string): ProductComponent =>
  Object.freeze({ key, name, question: `Does the customer use ${name}?` })

export const PRODUCT_COMPONENTS: Readonly<Record<ProductFamily, readonly ProductComponent[]>> = Object.freeze({
  traverse: Object.freeze([
    component('wms', 'WMS'),
    component('travApiPortal', 'Trav API Portal'),
    component('webApiPortal', 'Web API Portal'),
    component('adminPortal', 'Admin Portal'),
  ]),
  'process-pro': Object.freeze([]),
})

/** Every component key there is, whatever the product: what the store and the payload know about. */
export const ALL_COMPONENT_KEYS: readonly TraverseComponentKey[] = Object.freeze(['wms', 'travApiPortal', 'webApiPortal', 'adminPortal'])

/** The components a product includes, in display order. None for a product that is not a product line. */
export function componentsFor(product: ProductSubject): readonly ProductComponent[] {
  const family = productFamily(product)
  return family ? PRODUCT_COMPONENTS[family] : []
}
